import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Check,
  CheckCircle2,
  Copy,
  Loader2,
  Server,
  Terminal,
} from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useClipboard } from "use-clipboard-copy";
import {
  checkAutomationServerOTPStatus,
  checkSubdomainAvailability,
} from "@/data/automation-server";
import { useCreateAutomationServer, useDeleteAutomationServer } from "@/hooks/useAutomationServersQuery";
import { useQuery } from "@tanstack/react-query";
import { fetchServerPricing } from "@/data/billing";
import { money } from "@/types/offer";
import { cn } from "@/lib/utils";

interface ConnectAutomationServerModalProps {
  children?: React.ReactNode;
  onServerCreated?: (serverId: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  orgId?: string;
}

type DomainMode = "subdomain" | "custom";

// How the world gets to this server. Deliberately NOT phrased as "public vs
// private": a server on the relay is reached at a public hostname either way,
// and calling that option "publicly reachable" would suggest we point an A
// record at the operator's own public IP — which we don't do.
type Reachability = "relay" | "private";

// Bailey publishes the address the AOC records, so a typo here becomes a DNS
// record nobody can reach. The CLI refuses a malformed --private-address too,
// but only after the OTP has been spent, and registration cannot be re-run.
function isIPv4(value: string): boolean {
  const parts = value.trim().split(".");
  if (parts.length !== 4) return false;
  return parts.every(
    (part) => /^\d{1,3}$/.test(part) && Number(part) <= 255 && String(Number(part)) === part,
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function ConnectAutomationServerModal({
  children,
  onServerCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  orgId,
}: ConnectAutomationServerModalProps) {
  const [serverName, setServerName] = useState("");
  const [domainMode, setDomainMode] = useState<DomainMode>("subdomain");
  const [subdomain, setSubdomain] = useState("");
  const [subdomainEdited, setSubdomainEdited] = useState(false);
  const [customDomain, setCustomDomain] = useState("");
  const [reachability, setReachability] = useState<Reachability>("relay");
  const [privateAddress, setPrivateAddress] = useState("");
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);

  const [copied, setCopied] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [isCreating, setIsCreating] = useState(false);
  const [otp, setOtp] = useState<string | null>(null);
  const [automationServerId, setAutomationServerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [otpRedeemed, setOtpRedeemed] = useState(false);
  const [connectedDomain, setConnectedDomain] = useState<string | null>(null);
  const [domainStatus, setDomainStatus] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { mutate: createAutomationServer } = useCreateAutomationServer(orgId);
  const pricing = useQuery({
    queryKey: ["server-pricing", orgId ?? "active"],
    queryFn: () => fetchServerPricing(orgId),
    enabled: open,
  });
  const deleteServer = useDeleteAutomationServer();
  const [isCancelling, setIsCancelling] = useState(false);

  // Base backend URL (without /api/frontend) used both in the CLI command and
  // as the source of the install script the curl fetches.
  const backendHost = (
    typeof window !== "undefined" ? window.location.hostname : ""
  ).replace(/^aoc\./, "api.");
  const apiUrl = `https://${backendHost}`;

  const slug = slugify(subdomain);
  const resolvedDomain =
    domainMode === "subdomain"
      ? slug
        ? `${slug}.bswn.io`
        : ""
      : customDomain.trim();

  const clipboard = useClipboard({
    onSuccess() {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
    onError() {
      console.error("Failed to copy to clipboard");
    },
  });

  // Keep the subdomain in sync with the server name until the user edits it.
  useEffect(() => {
    if (!subdomainEdited && serverName) {
      setSubdomain(slugify(serverName));
    }
  }, [serverName, subdomainEdited]);

  // Debounced *.bswn.io availability check.
  useEffect(() => {
    if (domainMode !== "subdomain" || !slug) {
      setSubdomainAvailable(null);
      setSubdomainError(null);
      setIsCheckingSubdomain(false);
      return;
    }
    setSubdomainAvailable(null);
    setSubdomainError(null);
    setIsCheckingSubdomain(true);
    const timeout = setTimeout(async () => {
      try {
        const result = await checkSubdomainAvailability(slug);
        setSubdomainAvailable(result.available);
        setSubdomainError(result.error || null);
      } catch {
        setSubdomainError("Could not check availability");
      } finally {
        setIsCheckingSubdomain(false);
      }
    }, 500);
    return () => {
      clearTimeout(timeout);
      setIsCheckingSubdomain(false);
    };
  }, [slug, domainMode]);

  // Poll for the server phoning home after the curl runs.
  useEffect(() => {
    const checkOTPStatus = async () => {
      if (!automationServerId || otpRedeemed) return;
      try {
        const data = await checkAutomationServerOTPStatus(automationServerId);
        if (data.redeemed) {
          setOtpRedeemed(true);
          setIsCheckingStatus(false);
          setConnectedDomain(data.domain ?? null);
          setDomainStatus(data.domain_status ?? null);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          // Auto-close shortly after a successful connect (long enough to
          // read the confirmation).
          setTimeout(() => {
            handleOpenChange(false);
            if (onServerCreated && automationServerId) {
              onServerCreated(automationServerId);
            }
          }, 2500);
        }
      } catch (err) {
        console.error("Failed to check OTP status:", err);
      }
    };

    if (otp && automationServerId && !otpRedeemed) {
      setIsCheckingStatus(true);
      checkOTPStatus();
      intervalRef.current = setInterval(checkOTPStatus, 3000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, automationServerId, otpRedeemed]);

  const domainValid =
    domainMode === "subdomain"
      ? !!slug && subdomainAvailable === true && !isCheckingSubdomain
      : customDomain.trim().length > 0;

  const isPrivate = reachability === "private";
  // A private server with no address is one nobody can reach, and the relay is
  // not a fallback for it — so the address is required, not optional.
  const reachabilityValid = !isPrivate || isIPv4(privateAddress);

  const handleCreateServer = () => {
    if (!serverName.trim()) {
      setError("Server name is required");
      return;
    }
    setIsCreating(true);
    setError(null);
    createAutomationServer(
      { name: serverName, domain: resolvedDomain || undefined },
      {
        onSuccess: (data) => {
          setOtp(data.otp);
          setAutomationServerId(data.automation_server_id);
          setIsCreating(false);
        },
        onError: (err: any) => {
          setError(
            err.response?.data?.error ||
              err.message ||
              "Failed to create automation server",
          );
          setIsCreating(false);
        },
      },
    );
  };

  // ONE command sets up the whole server: the installer fetches the
  // arch-appropriate bitswan CLI from this AOC and then `exec`s `bitswan
  // register`, which brings up the daemon, learns its domain from the AOC,
  // stands up the ingress + protected proxy, and connects. No prerequisites,
  // no multi-step orchestration.
  //
  // Download the installer to a file FIRST, then run it under sudo — never
  // `curl … | sudo sh`. Piping into sudo is racy: a mistyped/aborted sudo
  // password tears down the pipe and surfaces as a baffling `curl: (23)` write
  // error instead of "wrong password". With `-o` + `&&`, curl completes as your
  // user before sudo is involved, so auth failures fail cleanly. Write it into
  // $HOME (a path you own) rather than /tmp, so a stale root-owned /tmp file
  // from an earlier run can't block the non-root curl from overwriting it.
  const registerCommand =
    otp && automationServerId
      ? `curl -fsSL ${apiUrl}/api/automation_server/install.sh -o ~/bitswan-install.sh && ` +
        `sudo sh ~/bitswan-install.sh ` +
        `--name "${serverName}" --aoc-api "${apiUrl}" ` +
        `--otp "${otp}" --server-id "${automationServerId}"` +
        // The AOC cannot discover the address of a server it cannot reach, so
        // the server declares it. --private is also a hard local pin: the daemon
        // then refuses the relay on every boot, whatever the AOC later says.
        (isPrivate ? ` --private --private-address "${privateAddress.trim()}"` : "") +
        // A custom domain on a private network can reach no CA — our bridge
        // cannot write its DNS-01 challenge, and HTTP-01 has no inbound route to
        // use. Settling the mode here keeps Traefik from ever opening an order it
        // cannot complete, which is otherwise a registration that fails outright.
        (isPrivate && domainMode === "custom" ? ` --tls-mode manual` : "")
      : "";

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setServerName("");
      setDomainMode("subdomain");
      setSubdomain("");
      setSubdomainEdited(false);
      setCustomDomain("");
      setReachability("relay");
      setPrivateAddress("");
      setSubdomainAvailable(null);
      setSubdomainError(null);
      setIsCheckingSubdomain(false);
      setCopied(false);
      setOtp(null);
      setAutomationServerId(null);
      setError(null);
      setIsCheckingStatus(false);
      setOtpRedeemed(false);
      setConnectedDomain(null);
      setDomainStatus(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Explicit Cancel while a server is created-but-unregistered: delete the
  // pending server so it doesn't linger in the list. (Just closing the dialog
  // keeps it — the user may still run the command later — and the server-side
  // purge reaps it once the OTP lapses. Cancel is the deliberate "never mind".)
  const handleCancel = async () => {
    if (automationServerId) {
      setIsCancelling(true);
      try {
        await deleteServer.mutateAsync(automationServerId);
      } catch (err) {
        // Non-fatal: the backend purges an unregistered server once its OTP
        // expires, so a failed delete still self-heals. Surface, don't block.
        console.error("Failed to cancel pending automation server:", err);
      } finally {
        setIsCancelling(false);
      }
    }
    handleOpenChange(false);
  };

  const created = !!otp && !!automationServerId;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="flex max-h-[85vh] flex-col overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Connect Bailey Automation Server
          </DialogTitle>
          <DialogDescription>
            {otpRedeemed
              ? "Your Bailey server is connected."
              : created
                ? "Run the command below on your Linux server. It installs Bailey and registers it here — this dialog updates automatically."
                : "Name your Bailey automation server and pick a domain, then run the generated command on your server to install and register it."}
          </DialogDescription>
        </DialogHeader>

        {otpRedeemed ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              <div className="text-sm text-green-900">
                <strong>Connected.</strong>{" "}
                {domainStatus === "private" && connectedDomain ? (
                  <>
                    <code className="font-mono">{connectedDomain}</code> points at{" "}
                    <code className="font-mono">{privateAddress.trim()}</code> and is reachable only
                    on your network — never through the relay.
                  </>
                ) : domainStatus === "active" && connectedDomain ? (
                  <>
                    <code className="font-mono">{connectedDomain}</code> is live and points at your server.
                  </>
                ) : (
                  "Your Bailey automation server is now registered."
                )}
              </div>
            </div>
            <Button onClick={() => handleOpenChange(false)} className="w-full">
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Once the OTP exists the choices are locked in — the command below
                already encodes them — so showing them again is noise between the
                operator and the one thing they still have to do. */}
            {!created && (
              <>
                <div className="space-y-2">
                  <label htmlFor="server-name" className="text-sm font-medium">
                    Server name
                  </label>
                  <Input
                    id="server-name"
                    placeholder="my-bailey-server"
                    value={serverName}
                    onChange={(e) => setServerName(e.target.value)}
                    disabled={created}
                  />
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium">Domain</span>
                  <div className="flex flex-col gap-2">
                    <ChoiceCard
                      selected={domainMode === "subdomain"}
                      onSelect={() => setDomainMode("subdomain")}
                      disabled={created}
                      title="Free bswn.io subdomain"
                      subtitle="Auto-registered for you — no DNS setup needed."
                    >
                      {domainMode === "subdomain" && (
                        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex">
                            <Input
                              value={subdomain}
                              onChange={(e) => {
                                setSubdomain(e.target.value);
                                setSubdomainEdited(true);
                              }}
                              placeholder="my-server"
                              disabled={created}
                              className="rounded-r-none"
                            />
                            <span className="flex items-center rounded-r-md border border-l-0 border-input bg-muted px-3 font-mono text-sm text-muted-foreground">
                              .bswn.io
                            </span>
                          </div>
                          <SubdomainHint
                            slug={slug}
                            checking={isCheckingSubdomain}
                            available={subdomainAvailable}
                            error={subdomainError}
                          />
                        </div>
                      )}
                    </ChoiceCard>

                    <ChoiceCard
                      selected={domainMode === "custom"}
                      onSelect={() => setDomainMode("custom")}
                      disabled={created}
                      title="Bring your own domain"
                      subtitle="Use a domain you already own for this server."
                    >
                      {domainMode === "custom" && (
                        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={customDomain}
                            onChange={(e) => setCustomDomain(e.target.value)}
                            placeholder="example.com"
                            disabled={created}
                          />
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            Point a DNS record for this domain at your server after registration.
                          </p>
                        </div>
                      )}
                    </ChoiceCard>
                  </div>
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium">How it&apos;s reached</span>
                  <div className="flex flex-col gap-2">
                    <ChoiceCard
                      selected={reachability === "relay"}
                      onSelect={() => setReachability("relay")}
                      disabled={created}
                      title="Through Bitswan's relay"
                      subtitle="The default. Works behind NAT with no inbound firewall rules."
                    />

                    <ChoiceCard
                      selected={reachability === "private"}
                      onSelect={() => setReachability("private")}
                      disabled={created}
                      title="Private network (VPN / LAN)"
                      subtitle="Reachable only on your own network. Never published through the relay."
                    >
                      {reachability === "private" && (
                        <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={privateAddress}
                            onChange={(e) => setPrivateAddress(e.target.value)}
                            placeholder="10.8.0.7"
                            disabled={created}
                            aria-label="Address clients reach this server on"
                          />
                          {privateAddress.trim() !== "" && !isIPv4(privateAddress) ? (
                            <p className="mt-1.5 text-xs text-destructive">
                              Enter an IPv4 address — this is published in DNS as-is.
                            </p>
                          ) : (
                            <p className="mt-1.5 text-xs text-muted-foreground">
                              The address clients reach this server on. We can&apos;t discover it, so
                              it&apos;s published exactly as entered.
                            </p>
                          )}
                          <p className="mt-1.5 text-xs text-muted-foreground">
                            {domainMode === "subdomain"
                              ? "We'll point this subdomain at that address, and certificates still issue automatically."
                              : "You'll create the DNS records and install certificates yourself."}
                          </p>
                        </div>
                      )}
                    </ChoiceCard>
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="rounded-md bg-destructive/10 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {!created && (
              <div className="space-y-3">
                {pricing.data && (
                  <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="byos-license-fee">
                    {pricing.data.license_monthly_price && pricing.data.currency
                      ? `${money(pricing.data.license_monthly_price, pricing.data.currency)} a month on your ${pricing.data.tier_name} licence, on the monthly BitSwan invoice.`
                      : `No licence fee on your ${pricing.data.tier_name} licence at the moment.`}
                    {pricing.data.free_until && (
                      <> {`Free until ${new Date(pricing.data.free_until).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })} while your trial runs.`}</>
                    )}
                  </p>
                )}
                <Button
                  onClick={handleCreateServer}
                  disabled={isCreating || !serverName.trim() || !domainValid || !reachabilityValid}
                  className="w-full"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    "Connect"
                  )}
                </Button>
              </div>
            )}

            {created && (
              <div className="space-y-3 rounded-lg border bg-muted/40 p-3.5">
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Run this on your Linux server
                  </span>
                </div>
                <div className="flex items-stretch overflow-hidden rounded-md border bg-background">
                  <code className="min-w-0 flex-1 whitespace-pre-wrap break-all px-3 py-2 font-mono text-[13px] leading-5">
                    {registerCommand}
                  </code>
                  <button
                    type="button"
                    title="Copy"
                    onClick={() => clipboard.copy(registerCommand)}
                    className="flex w-10 shrink-0 items-center justify-center self-stretch border-l text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {copied ? (
                      <Check className="h-[15px] w-[15px] text-green-600" />
                    ) : (
                      <Copy className="h-[15px] w-[15px]" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Installs the bitswan CLI and registers this server in one step
                  — nothing needs to be set up beforehand. Requires Docker.
                </p>
                {isPrivate && domainMode === "custom" && (
                  <p className="text-xs text-muted-foreground">
                    This server serves a certificate you supply. If you already have one, add{" "}
                    <code className="font-mono">--certs-dir /path/to/certs</code> to the command and
                    it is installed during registration — otherwise install it afterwards with{" "}
                    <code className="font-mono">bitswan ingress tls install-cert</code>. Until then
                    the server answers with a temporary self-signed certificate.
                  </p>
                )}
                {isCheckingStatus && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Waiting for your server to register…
                  </div>
                )}
              </div>
            )}

            {created && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isCancelling}
                className="w-full"
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelling…
                  </>
                ) : (
                  "Cancel"
                )}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChoiceCard({
  selected,
  onSelect,
  disabled,
  title,
  subtitle,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  title: string;
  subtitle: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === " " || e.key === "Enter")) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex gap-3 rounded-lg border p-3 transition-colors",
        disabled
          ? "cursor-default opacity-70"
          : "cursor-pointer hover:border-muted-foreground/40",
        selected ? "border-primary bg-primary/5" : "border-input",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border",
          selected ? "border-primary" : "border-muted-foreground/40",
        )}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-0.5 text-[13px] leading-[18px] text-muted-foreground">
          {subtitle}
        </div>
        {children}
      </div>
    </div>
  );
}

function SubdomainHint({
  slug,
  checking,
  available,
  error,
}: {
  slug: string;
  checking: boolean;
  // eslint-disable-next-line no-restricted-syntax -- null = not checked yet
  available: boolean | null;
  // eslint-disable-next-line no-restricted-syntax -- null = no error
  error: string | null;
}) {
  let text = "Enter a subdomain to check availability";
  let className = "text-muted-foreground";
  if (slug) {
    if (checking) {
      text = `Checking ${slug}.bswn.io…`;
    } else if (available === true) {
      text = `${slug}.bswn.io is available`;
      className = "text-green-600";
    } else if (available === false) {
      text = error || `${slug}.bswn.io is taken`;
      className = "text-destructive";
    } else if (error) {
      text = error;
      className = "text-destructive";
    }
  }
  return <p className={cn("mt-1.5 text-xs", className)}>{text}</p>;
}
